/**
 * HER TICKET, ALWAYS IN REACH
 * ===========================
 *
 * The way into her place: a small ticket docked bottom-right with her face on
 * it, rendered in the overlay host so it floats over the page and never under
 * the site's header. It replaces putting her places in the site's login
 * drawer (Colton, 2026-10-13): the drawer is the site's, and on a phone it is
 * a page, not a sheet. The welcome curtain flies her ticket into this.
 *
 * One face per place in the household, overlapped. A tap opens the place
 * sheet; with more than one place the sheet switches between them.
 *
 * A place on its way to someone else says so here too, so it reads at a
 * glance from the page: a marigold arrow on her face, a dashed marigold rule
 * instead of the cream one, and "Offered" (or "Moving", once the team
 * approves) where it said "Your ticket". `landKey` pops the arrow once, when
 * the sheet she sent it from closes and the ticket is back in view.
 *
 * z-45 keeps it under every sheet (desktop z-50, phone z-60) so an open sheet
 * covers it like the rest of the page instead of it sitting on the sheet's
 * bottom button.
 */
import { forwardRef } from 'react';
import { createPortal } from 'react-dom';
import { usePortalContainer } from '@church/nextjs-ui/bottom-sheet';
import { art } from '../lib/art';
import { Avatar } from './Avatar';

export const FloatingTicket = forwardRef<
  HTMLButtonElement,
  {
    people: { name: string; imageUrl: string | null; offer?: 'offered' | 'approved' | null }[];
    label: string;
    onOpen: () => void;
    /** Bumped when she closes the sheet she just gave a place away from. */
    landKey?: number;
  }
>(function FloatingTicket({ people, label, onOpen, landKey = 0 }, ref) {
  const portal = usePortalContainer();
  if (!people.length) return null;
  const shown = people.slice(0, 3);
  const pending = people.filter((p) => p.offer);
  const word = (o: 'offered' | 'approved') => (o === 'approved' ? 'Moving' : 'Offered');
  const kicker = !pending.length
    ? label
    : people.length === 1
      ? word(pending[0]!.offer!)
      : `${pending.length} ${pending.every((p) => p.offer === 'approved') ? 'moving' : 'offered'}`;
  const ticket = (
    <button
      ref={ref}
      type="button"
      onClick={onOpen}
      aria-label={`${label}: ${people
        .map((p) => (p.offer ? `${p.name}, ${word(p.offer).toLowerCase()}` : p.name))
        .join('; ')}`}
      className="wm-lift fixed right-4 bottom-[max(1rem,env(safe-area-inset-bottom))] z-[45] flex items-center gap-3 rounded-[10px] py-3 pr-6 pl-5 text-left shadow-[0_18px_40px_-16px_rgb(34_40_25/0.6)] sm:right-6 sm:bottom-6"
      style={{ fontFamily: "'Figtree', system-ui, sans-serif" }}
    >
      {/* The stub: the hero's watercolor, punched edges, a hairline inset. */}
      <span
        aria-hidden
        className="wm-ticket-edges wm-ticket-punched bg-wm-sage-950 absolute inset-0 overflow-hidden rounded-[10px]"
      >
        <img
          src={art('/art/watercolor.png')}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
        />
        <span className="bg-wm-sage-950/45 absolute inset-0" />
        <span
          className={`absolute inset-[5px] rounded-[6px] ${
            pending.length ? 'border-wm-marigold border border-dashed' : 'border-wm-cream/35 border'
          }`}
        />
      </span>
      <span className="relative flex items-center">
        {shown.map((p, i) => (
          <span
            key={`${p.name}-${i}`}
            className="ring-wm-cream/60 relative inline-flex rounded-full ring-2"
            style={{ marginLeft: i === 0 ? 0 : '-0.6rem', zIndex: shown.length - i }}
          >
            <Avatar name={p.name} imageSrc={p.imageUrl} size={34} />
            {p.offer ? (
              <span
                key={landKey}
                aria-hidden
                className={`bg-wm-marigold text-wm-sage-950 ring-wm-sage-950 absolute -right-1 -bottom-1 flex h-4 w-4 items-center justify-center rounded-full ring-2 ${
                  landKey ? 'wm-badge-pop' : ''
                }`}
              >
                <svg
                  viewBox="0 0 16 16"
                  className="h-2.5 w-2.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M3 8h10M9 4l4 4-4 4" />
                </svg>
              </span>
            ) : null}
          </span>
        ))}
      </span>
      <span className="relative leading-tight">
        <span
          className={`wm-kicker block text-[0.6rem] ${pending.length ? 'text-wm-marigold' : 'wm-kicker-on-dark'}`}
        >
          {kicker}
        </span>
        <span className="wm-display text-wm-cream block text-lg">
          {people.length === 1 ? people[0]!.name.split(' ')[0] : `${people.length} places`}
        </span>
      </span>
    </button>
  );
  return portal ? createPortal(ticket, portal) : ticket;
});
